import { formatApiError as formatSharedApiError } from "@ishvaram/api-client";
import { env } from "@/env";
import { fetchWithTimeout } from "@/lib/fetch";
import {
  RetryableHttpError,
  withResilientRetry,
  type RetryConfig,
  type TooManyRequestsPolicy,
} from "./_retry";

export const API_BASE = env.NEXT_PUBLIC_API_URL;
export { fetchWithTimeout };

// Normalize FastAPI error detail into a human-readable string.
// FastAPI returns `{detail: string}` for HTTPException and
// `{detail: [{loc, msg, type, ...}, ...]}` for Pydantic validation errors.
// Throwing `new Error(array)` coerces to "[object Object]" — this helper
// prevents that by flattening validation errors into readable text.
export function formatApiError(body: unknown, status: number): string {
  return formatSharedApiError(body, status);
}

export class ApiError extends Error {
  readonly status: number;
  /** The response's Retry-After in ms, when it sent a valid one. */
  readonly retryAfterMs: number | null;
  /**
   * The body's machine-readable `code` (the backend's ApplicationError answers
   * `{detail, code}`), so a page can localize the failure instead of showing
   * the English `detail`. Null when the body carries none.
   */
  readonly code: string | null;
  constructor(
    status: number,
    message: string,
    retryAfterMs: number | null = null,
    code: string | null = null
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retryAfterMs = retryAfterMs;
    this.code = code;
  }
}

function bodyCode(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const code = (body as { code?: unknown }).code;
  return typeof code === "string" && code ? code : null;
}

/**
 * RFC 9110 Retry-After: delay-seconds or an HTTP-date. Returns ms from `now`
 * (never negative), or null for an absent or malformed value.
 */
export function parseRetryAfterMs(
  value: string | null | undefined,
  now = Date.now()
): number | null {
  const raw = value?.trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) return Number(raw) * 1000;
  // An HTTP-date always names its day and month. Without that check V8's
  // lenient Date.parse would read "-5" or "5.5" as a date in 2001.
  if (!/[a-z]{3}/i.test(raw)) return null;
  const at = Date.parse(raw);
  return Number.isNaN(at) ? null : Math.max(0, at - now);
}

export async function throwApiError(res: Response, fallback?: string): Promise<never> {
  const body = await res.json().catch(() => ({}));
  throw new ApiError(
    res.status,
    formatApiError(body, res.status) || fallback || `HTTP ${res.status}`,
    parseRetryAfterMs(res.headers?.get("retry-after")),
    bodyCode(body)
  );
}

/** The governed first-party server-side identity.
 *
 * Every request we make from a Worker SSR render or the prerender box egresses
 * from ONE shared IP — the Cloudflare colo, or the build box. The zone
 * rate-limit rule keys on [cf.colo.id, ip.src], so all of our own server-side
 * traffic collapses onto a single 50-req/10s bucket while real browsers (each
 * with their own IP) sit at 0% 429. Measured 2026-07-29: SSR 28.2% 429,
 * prerender 46.9%, browsers 0%.
 *
 * Cloudflare's own documented fix is `cf.worker.upstream_zone`, which requires
 * a paid Advanced Rate Limiting plan we do not have. The entitled equivalent is
 * to make first-party server traffic *identifiable* and exclude it in the rule
 * expression — the standard "skip rule for known internal callers" pattern.
 *
 * Keep this in step with `first_party_user_agents` in
 * `business/ishvaram/data/ops/cloudflare_platform_policy.yaml`; the guard
 * `tests/test_cloudflare_platform_policy.py` pins them together.
 *
 * Browser requests never carry it — the browser controls its own UA, and a
 * seeker must stay subject to the limit.
 */
export const FIRST_PARTY_USER_AGENT = "IshvaramFirstParty/1.0";

const PRODUCTION_BUILD_PHASE = "phase-production-build";
let productionBuildOriginTail: Promise<void> = Promise.resolve();

function withFirstPartyIdentity(init?: RequestInit): RequestInit | undefined {
  // Browser fetch cannot set User-Agent (it is a forbidden header), and a
  // client request must be rate-limited like any other, so this is server-only.
  if (typeof window !== "undefined") return init;
  const headers = new Headers(init?.headers);
  headers.set("User-Agent", FIRST_PARTY_USER_AGENT);
  return { ...init, headers };
}

async function fetchApiOrigin(
  path: string,
  init?: RequestInit,
  timeoutMs?: number
): Promise<Response> {
  const request = () =>
    fetchWithTimeout(`${API_BASE}${path}`, withFirstPartyIdentity(init), timeoutMs);

  if (process.env.NEXT_PHASE !== PRODUCTION_BUILD_PHASE) return request();

  // A single static page can fan out to many expensive Jyotish reads. Next's
  // page-concurrency setting cannot bound that request fan-out, so serialize at
  // the origin boundary while production pages are being generated. The gate
  // is released in finally so one failed read cannot poison the build queue.
  const precedingRequest = productionBuildOriginTail;
  let releaseRequest!: () => void;
  productionBuildOriginTail = new Promise<void>((resolve) => {
    releaseRequest = resolve;
  });

  await precedingRequest;
  try {
    return await request();
  } finally {
    releaseRequest();
  }
}

export async function apiRequest<T>(
  path: string,
  init?: RequestInit,
  timeoutMs?: number
): Promise<T> {
  const res = await fetchApiOrigin(path, init, timeoutMs);
  if (!res.ok) await throwApiError(res);
  return res.json() as Promise<T>;
}

/**
 * How a server-side engine read rides out a 429. jyotish-api answers 429 when
 * Cloud Run has no instance free ("The request was aborted because there was
 * no available instance") — on 2026-10-05 07:02-07:04Z, 81 of them during a
 * site crawl, and each vrat page that met one failed closed with a 500 on the
 * first attempt. The two waits are full jitter up to 1 s and 2 s, raised to
 * Retry-After when the response sends one, and never more than 4 s in total.
 * A read that sees only 429s therefore ends within about 4 s plus three fast
 * rejections, well inside the render's 12 s engine timeout; a Retry-After
 * longer than the budget (the origin rate limiter sends up to 60 s) gives up
 * at once instead of waiting.
 */
export const SERVER_READ_TOO_MANY_REQUESTS: TooManyRequestsPolicy = {
  baseDelayMs: 500,
  maxDelayMs: 2_000,
  waitBudgetMs: 4_000,
};

/**
 * Retry policy for a deterministic server-side engine read (an idempotent GET).
 * A transport failure, timeout or 5xx is transient and is retried with
 * full-jitter backoff, and so is a 429 (`SERVER_READ_TOO_MANY_REQUESTS`). Any
 * other 4xx is final, because the same input fails the same way. When every
 * attempt fails the caller receives `RetryExhaustedError`. Never wrap a
 * mutating request in this: it would be retried.
 */
export function withServerReadRetry<T>(
  read: () => Promise<T>,
  overrides: Pick<RetryConfig, "sleep" | "random"> = {}
): Promise<T> {
  return withResilientRetry(
    async () => {
      try {
        return await read();
      } catch (error) {
        if (error instanceof ApiError && (error.status >= 500 || error.status === 429)) {
          throw new RetryableHttpError(error.status, error.message, error.retryAfterMs);
        }
        throw error;
      }
    },
    {
      idempotencyKey: "server-read",
      maxAttempts: 3,
      baseDelayMs: 100,
      maxDelayMs: 1_000,
      tooManyRequests: SERVER_READ_TOO_MANY_REQUESTS,
      ...overrides,
    }
  );
}

// Route deterministic, cacheable GET reads (panchang/choghadiya/muhurat —
// same (city,date) always yields the same result) through the same-origin
// Cloudflare edge proxy at /_jyotish/* so popular combinations are served
// from CF's cache instead of recomputing on Cloud Run every hit.
//
// SEO-safe by construction:
//   - client-only: SSR and unit tests use the direct origin API (a
//     worker->worker self-call would add latency, not save it);
//   - same-origin: no CORS, no new DNS/subdomain, no production routing change;
//   - graceful fallback: ANY proxy error falls back to the direct API, so a
//     page render can never break because the edge cache hiccuped.
//
// `fallbackOnUpstreamError: false` narrows that fallback to proxy/transport
// failures: when the edge proxy reports that the origin itself answered with
// an error (`x-edge-upstream-error: 1`, set by app/api/jyotish-edge), the read
// throws instead of asking the same failing engine again. Use it for reads
// made on every page view, where a direct retry would double origin load
// during an engine outage.
export interface EdgeReadOptions {
  fallbackOnUpstreamError?: boolean;
}

export async function edgeRead<T>(
  endpoint: string,
  search: string,
  timeoutMs?: number,
  { fallbackOnUpstreamError = true }: EdgeReadOptions = {}
): Promise<T> {
  const query = search ? `?${search}` : "";
  const directPath = `/${endpoint}${query}`;
  if (typeof window === "undefined") {
    return withServerReadRetry(() => apiRequest<T>(directPath, undefined, timeoutMs));
  }
  let upstreamError: ApiError | null = null;
  try {
    const res = await fetchWithTimeout(`/_jyotish/${endpoint}${query}`, undefined, timeoutMs);
    if (res.ok) return (await res.json()) as T;
    if (!fallbackOnUpstreamError && res.headers.get("x-edge-upstream-error") === "1") {
      upstreamError = new ApiError(res.status, `engine HTTP ${res.status} via edge proxy`);
    }
  } catch {
    // Proxy or transport failure: fall through to the direct API.
  }
  if (upstreamError) throw upstreamError;
  return apiRequest<T>(directPath, undefined, timeoutMs);
}

/**
 * A user-triggered edge read that reaches the engine at most once.
 *
 * edgeRead retries the origin directly after ANY proxy failure, which is right
 * for a page render but doubles engine load for an island: the proxy forwards
 * every miss and never caches an error, so an engine 404/4xx/429/5xx answered
 * through it was already one engine request, and the direct retry is a second.
 * Here an HTTP answer from the proxy (any status) is final, and so is a
 * timeout. Only a transport failure (the proxy itself unreachable) falls back
 * to the direct API, and only with the time left in the one budget, so the
 * seeker never waits longer than `timeoutMs` in total.
 */
function withSearch(path: string, search: string): string {
  return search ? `${path}?${search}` : path;
}

function isTimeout(err: unknown): boolean {
  const name = err instanceof Error || err instanceof DOMException ? err.name : "";
  return name === "TimeoutError" || name === "AbortError";
}

export async function edgeReadOnce<T>(
  endpoint: string,
  search: string,
  timeoutMs = 12_000
): Promise<T> {
  const directPath = withSearch(`/${endpoint}`, search);
  if (typeof window === "undefined") {
    return apiRequest<T>(directPath, undefined, timeoutMs);
  }
  const startedAt = Date.now();
  let res: Response;
  try {
    res = await fetchWithTimeout(withSearch(`/_jyotish/${endpoint}`, search), undefined, timeoutMs);
  } catch (err) {
    const left = timeoutMs - (Date.now() - startedAt);
    if (isTimeout(err) || left <= 0) throw err;
    return apiRequest<T>(directPath, undefined, left);
  }
  if (!res.ok) await throwApiError(res);
  return (await res.json()) as T;
}

export async function sameOriginRequest<T>(
  path: string,
  init?: RequestInit,
  timeoutMs?: number
): Promise<T> {
  const res = await fetchWithTimeout(path, init, timeoutMs);
  if (!res.ok) await throwApiError(res);
  return res.json() as Promise<T>;
}

export async function authJsonHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  try {
    const { getSupabase } = await import("@/lib/supabase");
    const {
      data: { session },
    } = await getSupabase().auth.getSession();
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }
  } catch {
    // Anonymous and test environments should still use the public API paths.
  }
  return headers;
}

export async function apiBlobRequest(
  path: string,
  init?: RequestInit,
  timeoutMs?: number
): Promise<Blob> {
  const res = await fetchApiOrigin(path, init, timeoutMs);
  if (!res.ok) await throwApiError(res);
  return res.blob();
}
